import { WarningIcon } from "@images/svgIcons/warningIcon"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { UserCell } from "@modules/members/components/userCell"
import { MemberItemEntity } from "@modules/members/entities/memberEntity"
import { MemberInputKey } from "@modules/members/formik/memberFormik"
import { MemberFilter } from "@modules/members/gateway/memberDto"
import { useMembers } from "@modules/members/hooks/useMember"
import { useMemberSideBar } from "@modules/members/hooks/useMemberSidebar"
import { convertLastVisitedToColor } from "@modules/members/memberUtils"
import { DataTable, renderTableColumns } from "@modules/ui/components/dataTables/datatable"
import { HuTextUnderline } from "@modules/ui/components/huLastVisited"
import { HuNoshow } from "@modules/ui/components/huNoshow"
import { HuNote } from "@modules/ui/components/huNote"
import { HUText } from "@modules/ui/components/huText"
import { formatDateWithHyphen } from "@modules/utils/dateUtils"
import dayjs from "dayjs"
import { Column, ColumnProps } from "primereact/column"
import { DataTableStateEvent, DataTableValue } from "primereact/datatable"
import { Skeleton } from "primereact/skeleton"
import { Tooltip } from "primereact/tooltip"
import { useEffect, useState } from "react"
import { DefaultTheme, useTheme } from "styled-components"

type Props = {
  columns?: ColumnProps[]
  page: number
  setPage: (p: number) => void
  size: number
  setSize: (s: number) => void
  hideHeaderOnEmptyLoading?: boolean
  empty: {
    full?: boolean
    view: React.ReactNode
  }
  selectedTab: MemberFilter
  nameFilter: string
  setCountTab: (count: number) => void
} & React.HTMLAttributes<HTMLDivElement>

export const MemberTable: React.FC<Props> = ({
  columns,
  page,
  setPage,
  size,
  setSize,
  empty,
  selectedTab,
  nameFilter,
  setCountTab,
}) => {
  const theme = useTheme()
  const language = useLanguage()
  const [selectedMember, setSelectedMember] = useState<DataTableValue>()
  const { navigateToMemberDetailsInfo } = useMemberSideBar()
  const diplayedInfoMember: MemberInputKey[] = ["name", "activeOffer", "lastVisit", "note", "noshow"]
  const { data, isLoading } = useMembers(selectedTab, page, size, nameFilter)

  useEffect(() => {
    if (data.totalSize) {
      setCountTab(data.totalSize)
    } else setCountTab(0)
  }, [data])

  const headerColumn = (key: MemberInputKey) => {
    switch (key) {
      case "name":
        return word("member.label.name")
      case "activeOffer":
        return word("box.member.table.activeOffer.label", { count: 1 })
      case "lastVisit":
        return word("box.member.table.lastVisit")
      case "assignedCoach":
        return word("box.member.table.assignedCoach.label")
      case "note":
        return word("box.member.table.note")
      case "noshow":
        return word("box.member.table.noShow", { count: 0 })
    }
  }

  const valueColumn = (key: MemberInputKey, member: MemberItemEntity) => {
    switch (key) {
      case "name":
        return (
          <UserCell
            member={{
              avatar: member.avatar,
              username: member.username,
              firstname: member.firstname,
              lastname: member.lastname,
            }}
            subtitle={member.getUserName()}
            subtitleColor={theme.neutralColor500}
          />
        )
      case "activeOffer":
        return (
          <div className="flex flex-row align-items-center">
            {member.activeOffers.length > 0 ? (
              <HUText color={theme.mainWhite} fontStyle="BM">
                {member.activeOffers.map((activeOffer, offerIndex) => {
                  const warning = activeOffer.getWarning(theme)
                  return (
                    <span
                      key={offerIndex}
                      className={`mr-1 ${warning ? "tooltip" + offerIndex : ""}`}
                      data-pr-showdelay={500}
                      data-pr-hidedelay={100}
                      data-pr-position="bottom"
                    >
                      {warning && (
                        <>
                          <WarningIcon
                            color={warning.warningColor}
                            width={20}
                            height={20}
                            style={{ verticalAlign: "text-bottom" }}
                            className="mr-1"
                          />
                          <Tooltip className="tooltip-container" target={`.tooltip${offerIndex}`}>
                            <div className="tooltip-content text-justify">
                              <HUText type="label" fontStyle="BM">
                                {warning.warningContent}
                              </HUText>
                            </div>
                          </Tooltip>
                        </>
                      )}

                      {`${activeOffer.offerName}${offerIndex < member.activeOffers.length - 1 ? "," : ""}`}
                    </span>
                  )
                })}
              </HUText>
            ) : (
              <div className={`flex flex-row align-items-center gap-1 mr-1`}>
                <WarningIcon color={theme.textWarning} width={24} height={24} />
                <HUText>{word("box.member.table.activeOffer.empty")}</HUText>
              </div>
            )}
          </div>
        )
      case "lastVisit":
        return member.lastVisited ? (
          <HuTextUnderline
            fontStyle="TS"
            color={theme.mainWhite}
            text={formatDateWithHyphen(dayjs(member.lastVisited).format("L"))}
            colorLine={convertLastVisitedToColor(theme, member.lastVisited)}
          />
        ) : (
          <HuTextUnderline fontStyle="TS" color={theme.mainWhite} text={word("box.member.table.noVisitYet")} />
        )
      case "assignedCoach":
        return "Martin Voegel"
      case "note":
        return <HuNote id={member.id} index={member.id} note={member.note ? member.note : ""} />
      case "noshow":
        return <HuNoshow number={member.noShowPastMonth || 0} />
      default:
        return <HUText>{word("global.error.label")}</HUText>
    }
  }

  const onPage = (event: DataTableStateEvent) => {
    setPage(event.page ?? 0)
    setSize(event.rows)
  }

  const onSelectedMember = (e: DataTableValue) => {
    navigateToMemberDetailsInfo(e.id as string, {
      onHide: () => {
        setSelectedMember(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    })
    setSelectedMember(e)
  }

  if (isLoading) {
    return (
      <div>
        <Skeleton width="100%" height="45rem" className="rounded-33" />
      </div>
    )
  }

  return (
    <div className="flex flex-column px-4">
      {!data || data.content.length <= 0 ? (
        empty.view
      ) : (
        <DataTable
          value={data.content as unknown as DataTableValue[]}
          dataKey="id"
          paginator
          lazy
          onPage={onPage}
          first={size * page}
          totalRecords={data.totalSize}
          rows={size}
          currentPageReportTemplate={""}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage={empty.view}
          loading={isLoading}
          selectionMode="single"
          selection={selectedMember}
          onSelectionChange={(e) => onSelectedMember(e.value)}
          unstyled={undefined}
          metaKeySelection={true}
          // size="small" // TODO CHANGE SIZE FOR SCREEN SIZE
        >
          {renderTableColumns(diplayedInfoMember, headerColumn, valueColumn, theme as DefaultTheme, language)}
          {columns?.map((columnProps, index) => <Column key={"more-column-" + index} {...columnProps} />)}
        </DataTable>
      )}
    </div>
  )
}
